import React, { useRef } from "react";
import { useSelector } from "react-redux";
import { Button, Form } from "antd";
import RadioGroup from "antd/lib/radio/group";
import ReactGA from "react-ga4";

import styles from "./postDeletionSurvey.scss";
import { AutoResizeTextArea } from "../AutoResizeTextArea";
import { LocalStorageKey, PageRoute, PostDeletionSurveyReason, getPostDeletionSurveyReasonOptions } from "../../models/enums";
import { api, localStorage, notification } from "../../services";
import confirmModal from "../../services/confirmModal";
import history from "../../history";
import { selectUserInfo } from "../../routes/Auth/auth.ducks";

const RenderPostDeletionSurveyForm = Form.create()(
  (props: any) => {
    const { form, formRef, category } = props;

    formRef.current = form;
    const options = getPostDeletionSurveyReasonOptions(category);
    return (
      <Form>
        <p>Kako biste nam pomogli poboljšati Index Oglase, recite nam zašto ste obrisali oglas?<br />Anketa je anonimna.<br /><br /></p>

        <Form.Item>
          {form.getFieldDecorator("reason", {
            rules: [{ required: true, message: "Molimo odaberite razlog" }],
          })(
            <RadioGroup
              className={styles.reasons}
              options={options}
            />
          )}
        </Form.Item>

        {form.getFieldsValue().reason === PostDeletionSurveyReason.other &&
          <Form.Item label="Navedite razlog">
            {form.getFieldDecorator("otherReason", {
              rules: [{ max: 300, message: "Najviše 300 znakova." }],
            })(
              <AutoResizeTextArea
                placeholder="Ukratko napišite zašto brišete oglas."
                rows={4}
                maxLength={300}
              />
            )}
          </Form.Item>
        }
      </Form>
    );
  }
);

const showFreePromotionGrantedModal = () => {
  ReactGA.event({
    category: "Besplatna promocija nakon brisanja oglasa",
    action: "open",
    label: "Prikazan modal"
  });

  const modal = confirmModal.show({
    title: "Želimo da Vaš sljedeći oglas ostvari bolje rezultate",
    iconType: "NaN",
    footer: (
      <Button
        type="primary"
        onClick={() => {
          ReactGA.event({
            category: "Besplatna promocija nakon brisanja oglasa",
            action: "click",
            label: "Objavi novi oglas"
          });

          modal.destroy();
          history.push(PageRoute.postAd);
        }}
        className={styles.button}
      >
        Objavi novi oglas
      </Button>
    ),
    children: (
      <React.Fragment>
        <p>Žao nam je što ovaj put niste ostvarili dovoljno pregleda ili interesa.</p>
        <p>Kako biste imali veće šanse za uspješnu prodaju, Vaš sljedeći oglas u istoj kategoriji automatski ćemo istaknuti na 30 dana, bez dodatne naknade.</p>
        <p>Promocija će se aktivirati automatski nakon objave sljedećeg oglasa.</p>
      </React.Fragment>
    )
  });
};

/**
 * Post-deletion survey shown after the user deletes their ad.
 * The modal is mounted outside the page tree, so it survives navigation
 * (e.g. redirecting away from the deleted ad's details page).
 */
export const usePostDeletionSurvey = () => {
  const user = useSelector(selectUserInfo);
  const formRef = useRef(null);
  const confirmModalContainer = useRef(null);

  const shouldShowPostDeleteSurvey = () => {
    // Check if the user has already answered the survey
    const posponedSurveyCount = localStorage.getItem(LocalStorageKey.posponedPostDeleteSurvey);
    const userParticipateInSurveys = user && user.participateInSurveys;

    // if the user has opted out of surveys, do not show the survey
    if (!userParticipateInSurveys) {
      return false;
    }

    // if there is no posponed survey count, return true to show the survey
    if (!posponedSurveyCount) {
      return true;
    }

    // if count is greater than 3 and timestamp is less than 30 days ago, do not show survey
    if (posponedSurveyCount.count > 2 && posponedSurveyCount.timestamp > Date.now() - 30 * 24 * 60 * 60 * 1000) {
      return false;
    }

    // if count is greater than 2 and timestamp is more than 30 days ago, reset count
    if (posponedSurveyCount.count > 2 && posponedSurveyCount.timestamp < Date.now() - 30 * 24 * 60 * 60 * 1000) {
      localStorage.setItem(
        LocalStorageKey.posponedPostDeleteSurvey,
        {
          count: 0,
          timestamp: Date.now()
        }
      );
    }

    // else show the survey
    return true;
  };

  const onPostDeletionSurveyFormSubmit = (adId: string) => {
    formRef.current.validateFields((err: any, values: any) => {
      if (!err) {
        const payload = { ...values, adId };
        api.Survey.adDeleteReason(payload)
          .then((response) => {
            // throw notification success
            notification.show("Uspješno poslano. Hvala na odgovoru.");

            // Log the survey submission event
            ReactGA.event({
              category: "Anketa brisanje oglasa",
              action: "click",
              label: "Odgovoreno"
            });

            // Destroy the confirm modal
            confirmModalContainer.current.destroy();

            // The answer earned the user a free promotion of their next ad in the same category
            if (response && response.freePromotionGranted) {
              showFreePromotionGrantedModal();
            }
          });
      }
    });
  };

  const onPostDeletionSurveyFormCancel = () => {
    const posponedSurveyCount = localStorage.getItem(LocalStorageKey.posponedPostDeleteSurvey);
    localStorage.setItem(
      LocalStorageKey.posponedPostDeleteSurvey,
      {
        count: posponedSurveyCount ? posponedSurveyCount.count + 1 : 1,
        timestamp: Date.now()
      }
    );

    // Log the cancellation event
    ReactGA.event({
      category: "Anketa brisanje oglasa",
      action: "click",
      label: "Nije odgovoreno"
    });

    // Destroy the confirm modal
    confirmModalContainer.current.destroy();
  };

  // Shows the survey for the deleted ad if conditions are met
  const showPostDeletionSurvey = (adId: string, category?: string) => {
    if (!adId || !shouldShowPostDeleteSurvey()) {
      return;
    }

    ReactGA.event({
      category: "Anketa brisanje oglasa",
      action: "open",
      label: "Prikazana anketa nakon brisanja"
    });

    confirmModalContainer.current = confirmModal.show({
      title: "Oglas je uspješno obrisan",
      okText: "Pošalji odgovor",
      cancelText: "Preskoči",
      iconType: "NaN",
      onCancel: () => onPostDeletionSurveyFormCancel(),
      footer: (
        <React.Fragment>
          <Button
            type="default"
            onClick={() => onPostDeletionSurveyFormCancel()}
            className={styles.button + " " + styles.skipButton}
          >
            Preskoči
          </Button>

          <Button
            type="primary"
            onClick={() => onPostDeletionSurveyFormSubmit(adId)}
            className={styles.button}
          >
            Pošalji odgovor
          </Button>
        </React.Fragment>
      ),
      children: <RenderPostDeletionSurveyForm formRef={formRef} category={category} />,
    });
  };

  return { showPostDeletionSurvey };
};



// WEBPACK FOOTER //
// ./src/components/PostDeletionSurvey/index.tsx